import { React, useState, useEffect } from 'react';
import { Modal, Button, Table, Icon } from 'semantic-ui-react';
import axios from 'axios';
import { formatTimestamp, getTimezoneAbbreviation } from '../utils/timezone.utils';
import { useTimezone } from '../hooks/useTimezone';
import { getBatchBadges, formatStatus } from '../utils/requestHelpers';

const RequestDetailsModal = ({
    open,
    onClose,
    request,
    // Approval-related props (optional)
    showApprovalActions = false,
    isApprover = false,
    onApprove = null,
    onReject = null,
    onMarkCompleted = null,
    processingApprovalId = null,
    processingRejectionId = null,
    isProcessing = false
}) => {
    const userTimezone = useTimezone(); // Listen to timezone changes for re-renders
    const [licenseModalOpen, setLicenseModalOpen] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [copyCloudNameSuccess, setCopyCloudNameSuccess] = useState(false);

    // Helper functions for readable labels
    const getVMSizeLabel = (value) => {
        const sizes = {
            'Standard_D2as_v5': '2vCPU-8GB RAM',
            'Standard_D4as_v5': '4vCPU-16GB RAM',
            'Standard_D8as_v5': '8vCPU-32GB RAM'
        };
        return sizes[value] || value || 'N/A';
    };

    const getLocationLabel = (location) => {
        if (!location) return 'N/A';

        const locationLower = location.toLowerCase();

        if (locationLower === 'centralindia') return 'Central India';
        if (locationLower === 'eastus') return 'East US';
        if (locationLower === 'westus3') return 'West US3';
        if (locationLower === 'uaenorth') return 'UAE North';
        if (locationLower === 'westus') return 'West US';

        return 'N/A';
    };

    const getStorageLabel = (value) => {
        const storageTypes = {
            'OD4B': 'OneDrive for Business (OD4B)',
            'Azure_Blob': 'Azure Blob',
            'S3_Compatibility': 'CPG DataCenter',
            'BYOS': 'BYOS',
            'Google_Drive': 'Google Drive',
            'Others_Storage': 'Others (AWS / Local Storage / S3-Compatible)'
        };
        return storageTypes[value] || value || 'N/A';
    };

    // Helper function to get setup type display
    const getSetupTypeLabel = (isMultiTenancySetup) => {
        if (isMultiTenancySetup === undefined || isMultiTenancySetup === null) {
            return 'Not Specified (Legacy Request)';
        }
        return isMultiTenancySetup ? 'Mixed Pod Setup' : 'Single Tenant Setup';
    };

    // Helper function to check if this is a Multi Tenancy Form submission
    const isRequestForm3 = (request) => {
        return request?.formData?.requestForm3 === true;
    };

    // Helper to display category, always showing PROD / POC for Multi Tenancy PROD
    const getCategoryLabel = (request) => {
        const category = request?.category;
        if (!category) return 'N/A';

        // For Multi Tenancy Form: always show PROD / POC for PROD category
        if (isRequestForm3(request) && category === 'PROD') {
            return 'PROD / POC';
        }

        return category;
    };

    // Get license file details (guard against null request)
    const licenseFile = request?.formData?.licenseFile || null;
    const fileName = licenseFile?.name || "Unknown File";

    const displayValue = (value) => (value && value !== "" ? value : 'N/A');

    // Helper function to format datetime-local value for display with user's timezone
    const formatDateTimeLocal = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        try {
            let date;

            // datetime-local format is YYYY-MM-DDTHH:mm (in user's browser timezone)
            // Parse it as local time first
            if (dateTimeString.includes('T')) {
                const [datePart, timePart] = dateTimeString.split('T');
                if (datePart && timePart) {
                    const [year, month, day] = datePart.split('-');
                    const [hours, minutes] = timePart.split(':');
                    // Create date in local timezone (browser timezone)
                    date = new Date(year, month - 1, day, hours, minutes);
                } else {
                    date = new Date(dateTimeString);
                }
            } else {
                date = new Date(dateTimeString);
            }

            if (isNaN(date.getTime())) {
                return dateTimeString;
            }

            // Format using user's selected timezone
            const formatted = date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
                timeZone: userTimezone
            });

            // Get timezone abbreviation for user's selected timezone
            const tzAbbr = getTimezoneAbbreviation(userTimezone, date);

            return `${formatted} ${tzAbbr}`;
        } catch (error) {
            return dateTimeString;
        }
    };

    // Helper function to get image version display text
    const getImageVersionDisplay = (imageVersion) => {
        if (!imageVersion) {
            return 'N/A';
        }
        // Map image version value to display text
        const versionMap = {
            'old': 'Platform Version 2.6.2',
            'old_2': 'Platform Version 2.6.3_4',
            'new': 'Platform Version 2.6.6'
        };
        return versionMap[imageVersion] || 'N/A';
    };

    // Keep a fresh copy when modal opens to avoid stale status after background updates
    const [latestRequest, setLatestRequest] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const fetchLatest = async () => {
            try {
                if (!open || !request?.id) return;
                const resp = await axios.get('https://qa-tenantbridge.parablu.com/api/requests', { withCredentials: true });
                const found = (resp.data?.requests || []).find(r => r.id === request.id);
                if (isMounted) setLatestRequest(found || null);
            } catch (e) {
                // ignore; fallback to passed-in request
            }
        };
        fetchLatest();
        return () => { isMounted = false; };
    }, [open, request?.id, request?.status]); // Also refresh when status changes

    const getField = (selectorFn, fallback) => {
        try {
            const value = selectorFn(latestRequest ?? request);
            return value !== undefined && value !== null ? value : fallback;
        } catch {
            return fallback;
        }
    };

    // Copy Request ID to clipboard
    const copyRequestId = async () => {
        const requestId = request?.id;
        if (!requestId) return;

        try {
            await navigator.clipboard.writeText(String(requestId));
            setCopySuccess(true);
            setTimeout(() => {
                setCopySuccess(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = requestId;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccess(true);
                setTimeout(() => {
                    setCopySuccess(false);
                }, 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const copyCloudName = async (text) => {
        const value = (text ?? '').toString();
        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);
            setCopyCloudNameSuccess(true);
            setTimeout(() => {
                setCopyCloudNameSuccess(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = value;
            textArea.style.position = 'fixed';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyCloudNameSuccess(true);
                setTimeout(() => {
                    setCopyCloudNameSuccess(false);
                }, 2000);
            } catch (fallbackErr) {
                console.error('Fallback copy failed:', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    // Helper function to parse details JSON and extract URLs
    const parseDetails = () => {
        try {
            const detailsStr = getField(r => r.details, null);
            if (!detailsStr) return null;

            if (typeof detailsStr === 'string' && detailsStr.startsWith('{')) {
                return JSON.parse(detailsStr);
            }
            return typeof detailsStr === 'object' ? detailsStr : null;
        } catch (e) {
            return null;
        }
    };

    // Extract URLs from request
    const getUrls = () => {
        const details = parseDetails();
        const urls = {};

        // Portal URL - check details first, then formData
        if (details?.portal?.url) {
            urls.portalUrl = details.portal.url;
        } else if (getField(r => r.formData?.portalUrl, null)) {
            urls.portalUrl = getField(r => r.formData?.portalUrl, null);
        }

        // Storage Blob Endpoint
        if (details?.storage?.blobEndpoint) {
            urls.storageBlobEndpoint = details.storage.blobEndpoint;
        }

        return urls;
    };

    const urls = getUrls();
    const details = parseDetails();
    const createdCloudName = (details?.cloudName ?? '').toString().trim();

    // Map status to text color
    const getStatusTextColor = (status) => {
        if (!status) return '#000';

        const normalized = String(status).toLowerCase();

        // Explicit matches
        if (normalized === 'withdrawn') return '#6b7280'; // Gray
        if (normalized === 'rejected') return '#CE1B4E'; // Red
        if (normalized.includes('cancelled') || normalized.includes('canceled')) return '#ea580c'; // Orange

        // Semantic contains
        if (normalized.includes('ready')) return '#6F9911'; // Green
        if (normalized.includes('in progress') || normalized.includes('created') || normalized.includes('creating') || normalized.includes('setup')) return '#35B7F4'; // Blue
        if (normalized.includes('failed') || normalized.includes('error')) return '#CE1B4E'; // Red

        // Default
        return '#062D4D';
    };

    // Keep hooks unconditional; defer null rendering until after hooks
    if (!request) return null;

    return (
        <Modal open={open} onClose={onClose} size="large">
            <Modal.Header>Request Details</Modal.Header>
            <Modal.Content>
                <Table definition>
                    <Table.Body>
                        {/* General Request Information */}
                        <Table.Row>
                            <Table.Cell width={4}>Request ID</Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>{displayValue(request.id)}</span>
                                    <Icon
                                        name={copySuccess ? "check" : "copy"}
                                        link
                                        onClick={copyRequestId}
                                        style={{
                                            cursor: 'pointer',
                                            color: copySuccess ? '#6F9911' : '#35B7F4',
                                            margin: 0,
                                            padding: 0,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            alignSelf: 'center'
                                        }}
                                        title={copySuccess ? "Copied!" : "Copy Request ID"}
                                    />
                                </div>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Submitted By</Table.Cell>
                            <Table.Cell>{displayValue(request.formData.submittedBy)}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Submission Time</Table.Cell>
                            <Table.Cell>
                                {formatTimestamp(request.timestamp)}
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Category</Table.Cell>
                            <Table.Cell>{displayValue(getCategoryLabel(request))}</Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Current Status</Table.Cell>
                            <Table.Cell style={{ color: getStatusTextColor(getField(r => r.status, null)), fontWeight: 600 }}>
                                {formatStatus(getField(r => r.status, null))}
                            </Table.Cell>
                        </Table.Row>

                        {/* Patch Details - Hidden for Multi Tenancy */}
                        {!isRequestForm3(request) && (
                            <>
                                <Table.Row>
                                    <Table.Cell colSpan={2} className="bg-gray-50">
                                        <strong>Patch Details</strong>
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Patch Version</Table.Cell>
                                    <Table.Cell>{getImageVersionDisplay(getField(r => r.formData?.imageVersion, null))}</Table.Cell>
                                </Table.Row>
                            </>
                        )}

                        {/* Tenant Details - Hidden for Multi Tenancy */}
                        {!isRequestForm3(request) && (
                            <>
                                <Table.Row>
                                    <Table.Cell colSpan={2} className="bg-gray-50">
                                        <strong>Tenant Details</strong>
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Tenant Name</Table.Cell>
                                    <Table.Cell>{displayValue(request.formData.tenantInfo?.name)}</Table.Cell>
                                </Table.Row>
                            </>
                        )}

                        {/* Form Data Section */}
                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>Form Details</strong>
                            </Table.Cell>
                        </Table.Row>

                        <Table.Row>
                            <Table.Cell>Form Type</Table.Cell>
                            <Table.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Icon
                                        name={isRequestForm3(request) ? 'users' : 'user'}
                                        color="blue"
                                    />
                                    <span>{isRequestForm3(request) ? 'Multi Tenancy Form' : 'Single Tenancy Form'}</span>
                                </div>
                            </Table.Cell>
                        </Table.Row>

                        {/* Hosting Type - Hidden for Multi Tenancy */}
                        {!isRequestForm3(request) && (
                            <Table.Row>
                                <Table.Cell>Hosting Type</Table.Cell>
                                <Table.Cell>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <Icon
                                            name={request.formData.isMultiTenancySetup ? 'users' : 'user'}
                                            color="blue"
                                        />
                                        <span>{getSetupTypeLabel(request.formData.isMultiTenancySetup)}</span>
                                    </div>
                                    {request.formData.isMultiTenancySetup === undefined && (
                                        <div style={{
                                            fontSize: '0.85em',
                                            color: '#666',
                                            fontStyle: 'italic',
                                            marginTop: '0.25rem'
                                        }}>
                                            This request was submitted before setup type selection was available.
                                        </div>
                                    )}
                                </Table.Cell>
                            </Table.Row>
                        )}

                        {/* For Multi Tenancy: Show Customer Name, Location, Storage Destination, Old Tenant URL, Migration Activity */}
                        {isRequestForm3(request) ? (
                            <>
                                {createdCloudName ? (
                                    <Table.Row>
                                        <Table.Cell>Cloud Name</Table.Cell>
                                        <Table.Cell>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span>{displayValue(createdCloudName)}</span>
                                                <Icon
                                                    name={copyCloudNameSuccess ? "check" : "copy"}
                                                    link
                                                    onClick={() => copyCloudName(createdCloudName)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        color: copyCloudNameSuccess ? '#6F9911' : '#35B7F4',
                                                        margin: 0,
                                                        padding: 0,
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        alignSelf: 'center'
                                                    }}
                                                    title={copyCloudNameSuccess ? "Copied!" : "Copy Cloud Name"}
                                                />
                                            </div>
                                        </Table.Cell>
                                    </Table.Row>
                                ) : null}
                                <Table.Row>
                                    <Table.Cell>Customer Name</Table.Cell>
                                    <Table.Cell>
                                        {displayValue(request.formData.cloudName)}
                                        {getBatchBadges(request).map((badge, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    marginLeft: idx === 0 ? '8px' : '4px',
                                                    padding: '2px 6px',
                                                    backgroundColor: '#e1f5fe',
                                                    color: '#35B7F4',
                                                    borderRadius: '3px',
                                                    fontSize: '0.8em',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {badge}
                                            </span>
                                        ))}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Location</Table.Cell>
                                    <Table.Cell>{getLocationLabel(request?.formData?.location?.location)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Storage Destination</Table.Cell>
                                    <Table.Cell>{getStorageLabel(request.formData.storageDestination)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>POC Setup</Table.Cell>
                                    <Table.Cell>
                                        {request.formData.isPOC === undefined || request.formData.isPOC === null
                                            ? 'Not Specified (Legacy Request)'
                                            : (request.formData.isPOC ? 'Yes' : 'No')}
                                    </Table.Cell>
                                </Table.Row>
                                {request.formData.oldTenantUrl && (
                                    <Table.Row>
                                        <Table.Cell>Old Tenant URL</Table.Cell>
                                        <Table.Cell>
                                            <a
                                                href={request.formData.oldTenantUrl.startsWith('http') ? request.formData.oldTenantUrl : `https://${request.formData.oldTenantUrl}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#35B7F4', textDecoration: 'underline' }}
                                            >
                                                {request.formData.oldTenantUrl}
                                                <Icon name="external alternate" style={{ marginLeft: '4px' }} />
                                            </a>
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                                <Table.Row>
                                    <Table.Cell>Migration Activity</Table.Cell>
                                    <Table.Cell>
                                        {request.formData.isMigrationSetup ? (
                                            "✅ Yes"
                                        ) : (
                                            "❌ No"
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                                {request.formData.isMigrationSetup && request.formData.migrationCompletionDate && (
                                    <Table.Row>
                                        <Table.Cell>Migration to be completed at or before</Table.Cell>
                                        <Table.Cell>{formatDateTimeLocal(request.formData.migrationCompletionDate)}</Table.Cell>
                                    </Table.Row>
                                )}
                            </>
                        ) : (
                            <>
                                <Table.Row>
                                    <Table.Cell>Cloud Name</Table.Cell>
                                    <Table.Cell>
                                        {displayValue(request.formData.cloudName?.toLowerCase())}
                                        {getBatchBadges(request).map((badge, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    marginLeft: idx === 0 ? '8px' : '4px',
                                                    padding: '2px 6px',
                                                    backgroundColor: '#e1f5fe',
                                                    color: '#35B7F4',
                                                    borderRadius: '3px',
                                                    fontSize: '0.8em',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {badge}
                                            </span>
                                        ))}
                                    </Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Location</Table.Cell>
                                    <Table.Cell>{getLocationLabel(request?.formData?.location?.location)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>VM Size</Table.Cell>
                                    <Table.Cell>{getVMSizeLabel(request.formData.vmSize)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Storage Destination</Table.Cell>
                                    <Table.Cell>{getStorageLabel(request.formData.storageDestination)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Azure Blob Hosted by CrashPlan?</Table.Cell>
                                    <Table.Cell>{getStorageLabel(request.formData.storageDestination) === "Azure Blob" && request.formData.azureBlobHosted ? 'Yes' : 'No'}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Disk Size (GB)</Table.Cell>
                                    <Table.Cell>{displayValue(request.formData.diskSizeGB)}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Azure Blob Fuse</Table.Cell>
                                    <Table.Cell>{request.formData.azureBlobFuse ? 'Yes' : 'No'}</Table.Cell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.Cell>Is this setup being created for migrating single tenancy to v2.6.x?</Table.Cell>
                                    <Table.Cell>
                                        {request.formData.isMigrationSetup ? (
                                            "✅ Yes"
                                        ) : (
                                            "❌ No"
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            </>
                        )}

                        {/* URLs Section - Hidden for RequestForm3 (multitenancy) */}
                        {!isRequestForm3(request) && (urls.portalUrl || urls.storageBlobEndpoint) && (
                            <>
                                <Table.Row>
                                    <Table.Cell colSpan={2} className="bg-gray-50">
                                        <strong>URLs</strong>
                                    </Table.Cell>
                                </Table.Row>
                                {urls.portalUrl && (
                                    <Table.Row>
                                        <Table.Cell>Portal URL</Table.Cell>
                                        <Table.Cell>
                                            <a
                                                href={urls.portalUrl.startsWith('http') ? urls.portalUrl : `https://${urls.portalUrl}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#35B7F4', textDecoration: 'underline' }}
                                            >
                                                {urls.portalUrl}
                                                <Icon name="external alternate" style={{ marginLeft: '4px' }} />
                                            </a>
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                                {urls.storageBlobEndpoint && (
                                    <Table.Row>
                                        <Table.Cell>Storage Blob Endpoint</Table.Cell>
                                        <Table.Cell>
                                            <a
                                                href={urls.storageBlobEndpoint.startsWith('http') ? urls.storageBlobEndpoint : `https://${urls.storageBlobEndpoint}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#35B7F4', textDecoration: 'underline' }}
                                            >
                                                {urls.storageBlobEndpoint}
                                                <Icon name="external alternate" style={{ marginLeft: '4px' }} />
                                            </a>
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </>
                        )}

                        {/* Approval Details */}
                        <Table.Row>
                            <Table.Cell colSpan={2} className="bg-gray-50">
                                <strong>Approval Details</strong>
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approver</Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const status = getField(r => r.status, null);
                                    if (status === 'WITHDRAWN') {
                                        return 'N/A';
                                    }
                                    // After approval, show only the approver who approved
                                    const approver = getField(r => r.approver, '');
                                    if (approver && approver !== '') {
                                        // Handle comma-separated approvers
                                        const approvers = approver.split(',').map(e => e.trim()).filter(e => e);
                                        if (approvers.length === 1) {
                                            return approvers[0];
                                        }
                                        return (
                                            <div>
                                                {approvers.map((email, index) => (
                                                    <div key={index} style={{ marginBottom: index < approvers.length - 1 ? '4px' : '0' }}>
                                                        {email}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }
                                    // While pending, show current approver list (if any)
                                    const currentApprover = getField(r => r.currentApprover, '');
                                    if (currentApprover && currentApprover !== '') {
                                        // Handle comma-separated approvers
                                        const approvers = currentApprover.split(',').map(e => e.trim()).filter(e => e);
                                        if (approvers.length === 1) {
                                            return approvers[0];
                                        }
                                        return (
                                            <div>
                                                {approvers.map((email, index) => (
                                                    <div key={index} style={{ marginBottom: index < approvers.length - 1 ? '4px' : '0' }}>
                                                        {email}
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }
                                    return 'N/A';
                                })()}
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approval Status</Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const status = getField(r => r.status, null);
                                    const normalizedStatus = status ? String(status).toLowerCase() : '';
                                    if (status === 'WITHDRAWN') {
                                        return 'N/A';
                                    }
                                    // Check for cancelled status
                                    if (normalizedStatus.includes('cancelled') || normalizedStatus.includes('canceled')) {
                                        const approverStatus = getField(r => r.approverStatus, '');
                                        return approverStatus && approverStatus !== '' ? approverStatus : 'CANCELLED';
                                    }
                                    const approverStatus = getField(r => r.approverStatus, '');
                                    if (approverStatus && approverStatus !== '') {
                                        return approverStatus;
                                    }
                                    if (status === 'PENDING_APPROVER') {
                                        return 'PENDING';
                                    }
                                    // Safeguard: treat terminal success with approver as APPROVED
                                    const approver = getField(r => r.approver, '');
                                    if (approver && (String(status).toLowerCase().includes('ready') || String(status).toLowerCase().includes('completed'))) {
                                        return 'APPROVED';
                                    }
                                    return 'N/A';
                                })()}
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>
                                {(() => {
                                    const status = getField(r => r.status, null);
                                    const normalizedStatus = status ? String(status).toLowerCase() : '';
                                    if (status === 'WITHDRAWN') {
                                        return 'Withdrawal Reason';
                                    } else if (normalizedStatus.includes('cancelled') || normalizedStatus.includes('canceled')) {
                                        return 'Cancelled By';
                                    }
                                    return 'Approver Comment';
                                })()}
                            </Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const status = getField(r => r.status, null);
                                    const comment = getField(r => r.approverComment, '');
                                    const normalizedStatus = status ? String(status).toLowerCase() : '';

                                    if (status === 'WITHDRAWN' && comment) {
                                        // Extract withdrawal reason from the comment
                                        // Format: "Withdrawn by [user]. Reason: [reason]" or 
                                        const reasonMatch = comment.match(/Reason:\s*(.+)$/i);
                                        if (reasonMatch && reasonMatch[1]) {
                                            return reasonMatch[1].trim();
                                        }
                                        // If format doesn't match, return the full comment
                                        return comment;
                                    } else if ((normalizedStatus.includes('cancelled') || normalizedStatus.includes('canceled')) && comment) {
                                        // For cancelled status, show who cancelled it
                                        // Format: "VM creation cancelled by [user]" or "Linode instance creation cancelled by [user]"
                                        const cancelledByMatch = comment.match(/cancelled by\s+(.+)$/i);
                                        if (cancelledByMatch && cancelledByMatch[1]) {
                                            return cancelledByMatch[1].trim();
                                        }
                                        // Fallback: show approver if comment doesn't match expected format
                                        const approver = getField(r => r.approver, '');
                                        return approver || comment;
                                    }
                                    return displayValue(comment);
                                })()}
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>Approver Timestamp</Table.Cell>
                            <Table.Cell>
                                {(() => {
                                    const status = getField(r => r.status, null);
                                    if (status === 'WITHDRAWN') {
                                        return 'N/A';
                                    }
                                    const approverTimestamp = getField(r => r.approverTimestamp, null);
                                    if (approverTimestamp) {
                                        return formatTimestamp(approverTimestamp);
                                    }
                                    return 'N/A';
                                })()}
                            </Table.Cell>
                        </Table.Row>

                    </Table.Body>

                </Table >
            </Modal.Content >
            <Modal.Actions>
                {showApprovalActions && onApprove && onReject && (
                    <>
                        <Button
                            positive
                            loading={processingApprovalId === request?.id}
                            disabled={isProcessing}
                            onClick={() => {
                                if (onApprove && request) {
                                    onApprove(request);
                                }
                            }}
                        >
                            Approve
                        </Button>
                        <Button
                            negative
                            loading={processingRejectionId === request?.id}
                            disabled={isProcessing}
                            onClick={() => {
                                if (onReject && request) {
                                    onReject();
                                }
                            }}
                        >
                            Reject
                        </Button>
                    </>
                )}
                {request?.status === 'MIGRATION IS IN PROGRESS' && onMarkCompleted && (
                    <Button
                        color="green"
                        loading={processingApprovalId === request?.id}
                        disabled={isProcessing}
                        onClick={() => {
                            if (onMarkCompleted && request) {
                                onMarkCompleted(request);
                            }
                        }}
                    >
                        Tenant is Ready
                    </Button>
                )}
                <Button onClick={onClose}>Close</Button>
            </Modal.Actions>

            {/* License File Details Modal */}
            <Modal open={licenseModalOpen} onClose={() => setLicenseModalOpen(false)} size="small">
                <Modal.Header>License File Details</Modal.Header>
                <Modal.Content>
                    <Table definition>
                        <Table.Body>
                            <Table.Row>
                                <Table.Cell>File Name</Table.Cell>
                                <Table.Cell>{fileName}</Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setLicenseModalOpen(false)}>Close</Button>
                </Modal.Actions>
            </Modal>
        </Modal >
    );
};

export default RequestDetailsModal;
